import { ExpandableSummarySection } from '@/components/edlink/expandable-summary-section';
import type { NormalizedEntityItem } from '@/lib/edlink-utils';
import type {
  CoachCsvApplySummary,
  CoachCsvCreateDelta,
  CoachCsvDryRunResult,
  CoachCsvFailure,
  CoachCsvSkip,
  CoachCsvUpdateDelta,
} from '@airreading/common/data';

type CoachCsvImportSummaryData = CoachCsvApplySummary & {
  totals?: CoachCsvDryRunResult['totals'];
};

interface CoachCsvImportSummaryProps {
  summary: CoachCsvImportSummaryData;
  isDryRun: boolean;
}

function createItems(creates: CoachCsvCreateDelta[]): NormalizedEntityItem[] {
  return creates.map((delta) => ({
    type: 'user',
    kind: 'create',
    label: `Row ${delta.row}: ${delta.email}${delta.displayName ? ` (${delta.displayName})` : ''}`,
    details: Object.fromEntries(
      Object.entries(delta.values)
        .filter(([, value]) => value != null && value !== '')
        .map(([key, value]) => [key, value as string]),
    ),
  }));
}

function updateItems(updates: CoachCsvUpdateDelta[]): NormalizedEntityItem[] {
  return updates.map((delta) => ({
    type: 'user',
    kind: 'update',
    label: `Row ${delta.row}: ${delta.email}`,
    details: Object.fromEntries(
      delta.changes.map((change) => [change.field, `${change.current ?? '(empty)'} → ${change.proposed ?? '(empty)'}`]),
    ),
  }));
}

function failureItems(failures: CoachCsvFailure[]): NormalizedEntityItem[] {
  return failures.map((failure) => ({
    type: 'user',
    kind: 'update',
    label: `Row ${failure.row}${failure.email ? `: ${failure.email}` : ''}`,
    details: { message: failure.message },
  }));
}

function skipItems(skips: CoachCsvSkip[]): NormalizedEntityItem[] {
  return skips.map((skip) => ({
    type: 'user',
    kind: 'update',
    label: `Row ${skip.row}: ${skip.email}`,
    details: { reason: skip.reason },
  }));
}

export function CoachCsvImportSummary({ summary, isDryRun }: CoachCsvImportSummaryProps) {
  return (
    <div className="flex flex-col gap-2">
      <div className="text-md mb-2 font-semibold">{isDryRun ? 'Dry Run preview' : 'Import summary'}</div>
      <ExpandableSummarySection
        label={isDryRun ? 'Teachers to create' : 'Teachers created'}
        color="border-l-green-500"
        items={createItems(summary.creates)}
      />
      <ExpandableSummarySection
        label={isDryRun ? 'Teachers to update' : 'Teachers updated'}
        color="border-l-blue-500"
        items={updateItems(summary.updates)}
      />
      <ExpandableSummarySection
        label={isDryRun ? 'Rows that will fail' : 'Rows that failed'}
        color="border-l-orange-500"
        items={failureItems(summary.failures)}
      />
      <ExpandableSummarySection
        label={isDryRun ? 'Rows that will be skipped' : 'Rows that were skipped'}
        color="border-l-gray-400"
        items={skipItems(summary.skips)}
      />
    </div>
  );
}
